/**
 * NicheReviewCard — channel-level diagnosis rendered above competitor video results.
 *
 * Shows:
 *   - Primary niche + focus score (0-10) with color-coded label
 *   - Topic distribution bars (overall, with recent/earlier drift comparison
 *     shown when the AI detected a shift)
 *   - Outlier video callouts — videos hurting YouTube's niche classification
 *     that the channel owner could unlist to sharpen focus
 *   - 1-sentence actionable verdict
 *
 * The API returns everything in one payload; this component just renders it.
 */
import React from "react";
import { Sparkles, Compass, TrendingUp, TrendingDown, AlertTriangle, Youtube, Loader2 } from "lucide-react";
import { Badge } from "./components/ui/badge";

function _colorForScore(score) {
  if (score >= 9) return { bar: "bg-emerald-500", text: "text-emerald-300", border: "border-emerald-500/30", bg: "bg-emerald-500/[0.08]" };
  if (score >= 7) return { bar: "bg-emerald-400", text: "text-emerald-300", border: "border-emerald-500/25", bg: "bg-emerald-500/[0.06]" };
  if (score >= 5) return { bar: "bg-amber-400",   text: "text-amber-300",   border: "border-amber-500/25",   bg: "bg-amber-500/[0.06]" };
  if (score >= 3) return { bar: "bg-orange-500",  text: "text-orange-300",  border: "border-orange-500/25",  bg: "bg-orange-500/[0.06]" };
  return          { bar: "bg-red-500",     text: "text-red-300",     border: "border-red-500/25",     bg: "bg-red-500/[0.06]" };
}

export function NicheReviewCard({ review, loading }) {
  if (loading) {
    return (
      <div
        className="mb-6 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 flex items-center gap-3 text-sm text-zinc-400"
        data-testid="niche-review-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin text-red-400" />
        Reading the last 50 videos to size up the niche + look for outliers…
      </div>
    );
  }
  if (!review) return null;

  const c = _colorForScore(review.focus_score);
  const topics = review.topics_overall || [];
  const maxPct = Math.max(1, ...topics.map((t) => t.coverage_pct || 0));

  return (
    <div
      className="mb-6 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 space-y-5 animate-fade-up"
      data-testid="niche-review-card"
    >
      {/* Header: primary niche + focus score */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2 mb-2">
            <Compass className="w-4 h-4 text-red-400" />
            <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
              Channel niche review · {review.videos_analysed} videos
            </span>
          </div>
          <h3 className="text-white font-semibold text-lg leading-snug mb-1" data-testid="niche-review-primary">
            {review.primary_niche}
          </h3>
          <p className="text-sm text-zinc-400 leading-relaxed" data-testid="niche-review-verdict">
            {review.verdict}
          </p>
        </div>
        <div className={`flex-shrink-0 rounded-xl border ${c.border} ${c.bg} px-4 py-3 min-w-[110px] text-center`}>
          <div className={`text-3xl font-bold ${c.text} leading-none`} data-testid="niche-review-focus-score">
            {review.focus_score}<span className="text-lg text-zinc-500 font-normal">/10</span>
          </div>
          <div className={`text-[10px] uppercase tracking-widest font-semibold mt-1.5 ${c.text}`}>
            {review.focus_label}
          </div>
        </div>
      </div>

      {/* Topic distribution — overall bars */}
      {topics.length > 0 && (
        <div data-testid="niche-review-topics">
          <div className="flex items-center gap-2 mb-2.5">
            <Sparkles className="w-3.5 h-3.5 text-zinc-500" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Topic coverage</span>
          </div>
          <div className="space-y-2">
            {topics.map((t, i) => (
              <div key={`${t.name}-${i}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-sm text-zinc-200">{t.name}</span>
                  <span className="text-xs text-zinc-400 tabular-nums">{Math.round(t.coverage_pct)}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                  <div
                    className={`h-full ${c.bar} transition-all duration-500`}
                    style={{ width: `${Math.min(100, (t.coverage_pct / maxPct) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Shift detection — recent vs earlier breakdown, only when the AI flagged it */}
      {review.shift_detected && review.topics_recent?.length > 0 && review.topics_earlier?.length > 0 && (
        <div
          className="rounded-xl border border-amber-500/20 bg-amber-500/[0.05] p-4"
          data-testid="niche-review-shift"
        >
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-amber-300" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-amber-200 font-semibold">Channel drift detected</span>
          </div>
          <p className="text-sm text-amber-100 mb-3 leading-relaxed" data-testid="niche-review-shift-summary">
            {review.shift_summary}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div className="rounded-lg bg-black/40 border border-white/5 p-3">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold mb-2">Recent half</div>
              <ul className="space-y-1">
                {review.topics_recent.slice(0, 5).map((t, i) => (
                  <li key={`r-${i}`} className="flex items-baseline justify-between gap-2">
                    <span className="text-zinc-300 truncate">{t.name}</span>
                    <span className="text-zinc-400 tabular-nums">{Math.round(t.coverage_pct)}%</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg bg-black/40 border border-white/5 p-3">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold mb-2">Earlier half</div>
              <ul className="space-y-1">
                {review.topics_earlier.slice(0, 5).map((t, i) => (
                  <li key={`e-${i}`} className="flex items-baseline justify-between gap-2">
                    <span className="text-zinc-300 truncate">{t.name}</span>
                    <span className="text-zinc-400 tabular-nums">{Math.round(t.coverage_pct)}%</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Outliers — videos that hurt niche classification */}
      {review.outliers?.length > 0 && (
        <div
          className="rounded-xl border border-red-500/20 bg-red-500/[0.05] p-4"
          data-testid="niche-review-outliers"
        >
          <div className="flex items-center gap-2 mb-2.5">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-red-300 font-semibold">
              {review.outliers.length} outlier{review.outliers.length === 1 ? "" : "s"} skewing niche classification
            </span>
          </div>
          <p className="text-xs text-zinc-400 mb-3">
            These videos don&rsquo;t fit the primary niche and may be confusing YouTube&rsquo;s algorithm about what this channel is. If it&rsquo;s your channel, consider unlisting or deleting them.
          </p>
          <ul className="space-y-2.5">
            {review.outliers.map((o, i) => (
              <li
                key={o.video_id}
                className="rounded-lg bg-black/40 border border-white/5 p-3"
                data-testid={`niche-review-outlier-${i}`}
              >
                <div className="flex items-start justify-between gap-3 mb-1">
                  <span className="text-sm text-zinc-200 font-medium leading-snug">{o.title}</span>
                  <a
                    href={`https://www.youtube.com/watch?v=${o.video_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 text-xs flex-shrink-0"
                    data-testid={`niche-review-outlier-${i}-link`}
                  >
                    <Youtube className="w-3.5 h-3.5" /> Watch
                  </a>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">{o.reason}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* No-drift positive signal */}
      {!review.shift_detected && (
        <div className="text-xs text-zinc-500 italic flex items-center gap-2" data-testid="niche-review-no-shift">
          <TrendingDown className="w-3.5 h-3.5" />
          No material drift between the recent and earlier halves — channel has stayed on-topic.
        </div>
      )}
    </div>
  );
}
